<script setup>
/**
 * 公会设置（/guilds/:slug/settings，负责人与运营）：公会名称，和飞书文档与任务树的对应表。
 * 一份飞书文档对应一棵任务树（一条连接）；同一个公会可以连多份。任务树页、任务列表都由同步下来的任务现算。
 * 仓库里的树文件、手工上传的树和归档公会也在这里，其余资料在「修改公会资料」页。
 */
import { computed, onMounted, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { Badge, Button, ConfirmAction, Input, Select, Table, Textarea } from '@drama/ui'

import { api, loadAccount } from '../api.js'

const { t } = useI18n()
const route = useRoute()
const base = `/api/voko/guilds/${encodeURIComponent(route.params.slug)}`
const guild = ref(null)
const connections = ref([])
const trees = ref([])
const name = ref('')
const message = ref('')
const error = ref('')
const busy = ref(false)
const treeForm = reactive({ slug: '', content: '' })
const feishuForm = reactive({ url: '', kr_table_id: '', root_record_id: '' })
const feishuPreview = ref(null)
watch(() => feishuForm.url, () => { feishuPreview.value = null; feishuForm.kr_table_id = ''; feishuForm.root_record_id = '' })

const when = (at) => new Date(at).toLocaleString()
const columns = computed(() => [
  { key: 'tree_slug', label: t('guildPages.settings.colTree'), width: '170px', mono: true },
  { key: 'root_title', label: t('guildPages.settings.colObjective'), width: '220px' },
  { key: 'last_synced_at', label: t('guildPages.settings.colSynced'), width: '170px' },
  { key: 'state', label: t('guildPages.settings.colState'), width: '220px', wrap: true },
  { key: 'actions', label: '', width: '230px' },
])
const otherTrees = computed(() => trees.value.filter((tree) => tree.source !== 'okr'))

async function load() {
  const [data, connectionData, treeData] = await Promise.all([api('GET', `${base}/manage`), api('GET', `${base}/trees/feishu/connections`), api('GET', `${base}/trees`)])
  guild.value = data.guild
  name.value = data.guild.name
  connections.value = connectionData.connections
  trees.value = treeData.trees
}
onMounted(async () => {
  try { await load() } catch (err) { error.value = err instanceof Error ? err.message : String(err) }
})

/**
 * @param {() => Promise<unknown>} action
 * @param {string} done 成功后的提示
 */
async function run(action, done) {
  if (busy.value) return
  busy.value = true
  message.value = ''
  error.value = ''
  try {
    await action()
    await Promise.all([load(), loadAccount()])
    message.value = done
  } catch (err) {
    error.value = err instanceof Error ? err.message : String(err)
  } finally {
    busy.value = false
  }
}

// PUT 要整份资料：名称之外的字段原样带回，只改名称。
const saveName = () => run(() => api('PUT', base, { name: name.value, description: guild.value.description, handbook: guild.value.handbook, github_repo: guild.value.github_repo,
  require_lead_approval: guild.value.require_lead_approval, join_policy: guild.value.join_policy }), t('guildPages.settings.saved'))

// 链接粘上去直接连：表和目标由服务端从链接和表内容定；定不了（422）才读出选项让人选。
const connectFeishu = () => run(async () => {
  const body = { url: feishuForm.url.trim(), ...(feishuForm.kr_table_id === '' ? {} : { kr_table_id: feishuForm.kr_table_id }), ...(feishuForm.root_record_id === '' ? {} : { root_record_id: feishuForm.root_record_id }) }
  try {
    await api('POST', `${base}/trees/feishu/connect`, body)
  } catch (err) {
    if (err.code !== 'rule_violation') throw err
    feishuPreview.value = await api('POST', `${base}/trees/feishu/preview`, { url: body.url })
    if (feishuForm.kr_table_id === '' && feishuPreview.value.table_id !== null) feishuForm.kr_table_id = feishuPreview.value.table_id
    throw err
  }
  feishuPreview.value = null
  Object.assign(feishuForm, { url: '', kr_table_id: '', root_record_id: '' })
}, t('guilds.manageForm.trees.feishuImported'))
/** @param {string} slug */
const syncFeishu = (slug) => run(() => api('POST', `${base}/trees/${encodeURIComponent(slug)}/feishu/sync`, {}), t('guilds.manageForm.trees.feishuSynced', { slug }))
/** @param {string} slug */
const disconnect = (slug) => run(() => api('DELETE', `${base}/trees/${encodeURIComponent(slug)}/feishu`), t('guildPages.settings.disconnected', { slug }))

// 手工上传的树：粘贴 JSON 或选一个 .json 文件；校验在服务端。
/** @param {Event} event */
async function pickTreeFile(event) {
  const file = /** @type {HTMLInputElement} */ (event.target).files[0]
  treeForm.content = await file.text()
  if (treeForm.slug === '') treeForm.slug = file.name.replace(/\.json$/, '')
}
const uploadTree = () => {
  const slug = treeForm.slug.trim()
  return run(async () => {
    await api('PUT', `${base}/trees/${encodeURIComponent(slug)}`, { content: treeForm.content })
    Object.assign(treeForm, { slug: '', content: '' })
  }, t('guilds.manageForm.trees.uploaded', { slug }))
}
/** @param {string} slug */
const removeTree = (slug) => run(() => api('DELETE', `${base}/trees/${encodeURIComponent(slug)}`), t('guilds.manageForm.trees.removed'))
const archive = () => run(() => api('POST', `${base}/archive`), t('guilds.manageForm.archivedNote'))
</script>

<template>
  <header class="page-head">
    <div>
      <p class="page-head__eyebrow">{{ t('shell.guildSettings') }}</p>
      <h1 class="page-head__title">{{ t('guildPages.settings.title') }}{{ guild === null ? '' : ` · ${guild.name}` }}</h1>
    </div>
  </header>
  <p v-if="message" role="status">{{ message }}</p>
  <p v-if="error" role="alert">{{ error }}</p>
  <p v-if="guild !== null && guild.status === 'archived'">{{ t('guilds.manageForm.archivedNote') }}</p>
  <template v-else-if="guild !== null">
    <section class="voko-panel" :aria-label="t('guildPages.settings.name')">
      <p class="voko-muted">{{ t('guildPages.settings.intro') }}</p>
      <form class="gs-row" @submit.prevent="saveName">
        <label class="voko-field">{{ t('guildPages.settings.name') }}<Input v-model="name" maxlength="120" required /></label>
        <Button type="submit" :disabled="busy || name.trim() === '' || name === guild.name">{{ t('guildPages.settings.save') }}</Button>
      </form>
      <router-link :to="`/guilds/${route.params.slug}/edit`">{{ t('guildPages.settings.moreProfile') }}</router-link>
    </section>

    <section class="voko-panel" :aria-label="t('guildPages.settings.docsTitle')">
      <h2>{{ t('guildPages.settings.docsTitle') }}</h2>
      <p class="voko-muted">{{ t('guildPages.settings.docsIntro') }}</p>
      <p v-if="connections.length === 0" class="voko-muted">{{ t('guildPages.settings.noDocs') }}</p>
      <Table v-else :columns="columns" :rows="connections">
        <template #tree_slug="{ row }"><router-link :to="`/trees/${row.tree_slug}`">{{ row.tree_slug }}</router-link></template>
        <template #last_synced_at="{ row }">{{ row.last_synced_at === null ? t('guildPages.settings.never') : when(row.last_synced_at) }}</template>
        <template #state="{ row }">
          <Badge v-if="row.last_error !== null" size="sm" variant="danger">{{ row.last_error }}</Badge>
          <Badge v-else-if="row.conflicts > 0" size="sm" variant="warning">{{ t('guildPages.settings.conflicts', { n: row.conflicts }) }}</Badge>
          <Badge v-else size="sm" variant="success">{{ t('guildPages.settings.ok') }}</Badge>
        </template>
        <template #actions="{ row }">
          <Button size="sm" variant="secondary" :disabled="busy" @click="syncFeishu(row.tree_slug)">{{ t('guilds.manageForm.trees.feishuSync') }}</Button>
          <ConfirmAction :label="t('guildPages.settings.disconnect')" :action="() => disconnect(row.tree_slug)" :confirm-text="t('guildPages.settings.disconnectConfirm', { slug: row.tree_slug })" :cancel-label="t('guildPages.common.cancel')" :ok-label="t('guildPages.settings.disconnect')" :pending-label="t('guilds.manageForm.working')" danger />
        </template>
      </Table>

      <form class="gs-form" @submit.prevent="connectFeishu">
        <h3>{{ t('guildPages.settings.addDoc') }}</h3>
        <p class="voko-muted">{{ t('guilds.manageForm.trees.feishuHelp') }}</p>
        <label class="voko-field">{{ t('guilds.manageForm.trees.feishuUrl') }}<Input v-model="feishuForm.url" type="url" maxlength="2048" required /></label>
        <template v-if="feishuPreview">
          <label v-if="feishuPreview.table_id === null" class="voko-field">{{ t('guilds.manageForm.trees.feishuKrTable') }}<Select v-model="feishuForm.kr_table_id" :options="feishuPreview.kr_tables.map((item) => ({ value: item.id, label: item.name }))" :placeholder="t('guilds.manageForm.trees.feishuPickKrTable')" required /></label>
          <label class="voko-field">{{ t('guilds.manageForm.trees.feishuObjective') }}<Select v-model="feishuForm.root_record_id" :options="feishuPreview.objectives.map((item) => ({ value: item.record_id, label: item.title }))" :placeholder="t('guilds.manageForm.trees.feishuPickObjective')" required /></label>
        </template>
        <p v-if="error" role="alert">{{ error }}</p>
        <Button type="submit" :disabled="busy || feishuForm.url.trim() === ''">{{ t('guilds.manageForm.trees.feishuImport') }}</Button>
      </form>
    </section>

    <section class="voko-panel" :aria-label="t('guildPages.settings.otherTrees')">
      <h2>{{ t('guildPages.settings.otherTrees') }}</h2>
      <p class="voko-muted">{{ t('guilds.manageForm.trees.intro') }}</p>
      <p v-if="otherTrees.length === 0" class="voko-muted">{{ t('guilds.manageForm.trees.empty') }}</p>
      <ul v-else class="gs-rows">
        <li v-for="tree in otherTrees" :key="tree.slug">
          <span><router-link :to="`/trees/${tree.slug}`"><strong>{{ tree.slug }}</strong></router-link> · {{ tree.title }}</span>
          <template v-if="tree.source === 'upload'">
            <span class="voko-muted">{{ t('guilds.manageForm.trees.updated', { at: when(tree.updated_at) }) }}</span>
            <Button size="sm" variant="ghost" @click="treeForm.slug = tree.slug">{{ t('guilds.manageForm.trees.replace') }}</Button>
            <ConfirmAction :label="t('guilds.manageForm.trees.remove')" :action="() => removeTree(tree.slug)" :confirm-text="t('guilds.manageForm.trees.removeConfirm', { slug: tree.slug })" :cancel-label="t('guildPages.common.cancel')" :ok-label="t('guilds.manageForm.trees.remove')" :pending-label="t('guilds.manageForm.working')" danger />
          </template>
          <span v-else class="voko-muted">{{ t('guilds.manageForm.trees.fileNote') }}</span>
        </li>
      </ul>
      <form class="gs-form" @submit.prevent="uploadTree">
        <label class="voko-field">{{ t('guilds.manageForm.trees.slug') }}<Input v-model="treeForm.slug" maxlength="63" required /></label>
        <label class="voko-field">{{ t('guilds.manageForm.trees.content') }}<Textarea v-model="treeForm.content" :rows="8" spellcheck="false" /></label>
        <label class="voko-field">{{ t('guilds.manageForm.trees.pickFile') }}<input type="file" accept=".json,application/json" @change="pickTreeFile"></label>
        <Button type="submit" :disabled="busy || treeForm.slug.trim() === '' || treeForm.content.trim() === ''">{{ t('guilds.manageForm.trees.submit') }}</Button>
      </form>
    </section>

    <section class="voko-panel" :aria-label="t('guildPages.settings.archiveTitle')">
      <h2>{{ t('guildPages.settings.archiveTitle') }}</h2>
      <p class="voko-muted">{{ t('guilds.manageForm.archiveIntro') }}</p>
      <ConfirmAction :label="t('guilds.manageForm.archive')" :action="archive" :confirm-text="t('guilds.manageForm.archiveConfirm')" :cancel-label="t('guildPages.common.cancel')" :ok-label="t('guilds.manageForm.archive')" :pending-label="t('guilds.manageForm.working')" danger />
    </section>
  </template>
</template>

<style scoped>
.voko-panel { margin-bottom: var(--drama-space-lg); }
.gs-row { display: flex; flex-wrap: wrap; align-items: flex-end; gap: var(--drama-space-sm); margin: var(--drama-space-sm) 0; }
.gs-row .voko-field { flex: 1 1 18rem; }
.gs-form { margin-top: var(--drama-space-md); }
.gs-form h3 { margin: 0 0 var(--drama-space-xs); font-size: var(--drama-font-size-sm); }
.gs-rows { margin: 0; padding: 0; list-style: none; }
.gs-rows li { display: flex; flex-wrap: wrap; align-items: center; gap: var(--drama-space-sm); padding: var(--drama-space-xs) 0; border-bottom: 1px solid var(--drama-color-border); }
.gs-rows li > span:first-child { flex: 1 1 16rem; }
</style>
